import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { MobileNav } from "../components/MobileNav";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { SERVICES, CITIES, pagePath, hubPath, SITE } from "../lib/seoPages";
import { useI18n } from "../i18n/LanguageContext";

const T = {
  en: {
    title: "Services & Destinations | Private Jets, Yachts, Villas, Chauffeur, Concierge — VELMONTÉ",
    desc: "Every VELMONTÉ service by destination: private jet charter, yacht charter, chauffeur, luxury villas and concierge in Mallorca, Ibiza, Marbella, Monaco, Marrakech and Dubai.",
    h1: "Services & destinations",
    intro: "One contact for private aviation, yachts, chauffeured mobility, villas and concierge — in the destinations where we work every week, and anywhere else on request.",
  },
  es: {
    title: "Servicios y Destinos | Jet Privado, Yates, Villas, Chófer, Concierge — VELMONTÉ",
    desc: "Todos los servicios de VELMONTÉ por destino: jet privado, alquiler de yates, chófer, villas de lujo y concierge en Mallorca, Ibiza, Marbella, Mónaco, Marrakech y Dubái.",
    h1: "Servicios y destinos",
    intro: "Un único contacto para aviación privada, yates, movilidad con chófer, villas y concierge — en los destinos donde trabajamos cada semana y en cualquier otro bajo petición.",
  },
};

export default function ServicesHub({ lang = "en" }) {
  const lg = lang === "es" ? "es" : "en";
  const t = T[lg];
  const { lang: uiLang, setLang } = useI18n();
  useEffect(() => { window.scrollTo(0, 0); }, [lg]);
  useEffect(() => { if (lg !== uiLang) setLang(lg); }, [lg, uiLang, setLang]);

  return (
    <div className="vm-grain relative">
      <Seo title={t.title} description={t.desc} path={hubPath(lg)} lang={lg}
        altPath={hubPath(lg === "es" ? "en" : "es")}
        jsonLd={[{
          "@context": "https://schema.org", "@type": "CollectionPage",
          name: t.h1, description: t.desc, url: `${SITE}${hubPath(lg)}`,
        }]} />
      <Header />
      <main className="pt-32 md:pt-40" data-testid="services-hub">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
          <Reveal>
            <h1 className="font-serif font-light leading-[1] text-4xl md:text-6xl mb-7">{t.h1}</h1>
            <p className="text-vm-text2 font-light leading-relaxed max-w-2xl text-base md:text-lg mb-16">{t.intro}</p>
          </Reveal>

          <div className="space-y-14">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.05}>
                <div className="border-t border-vm-border pt-8" data-testid={`hub-service-${s.id}`}>
                  <p className="vm-label mb-2">{s[lg].label}</p>
                  <h2 className="font-serif text-2xl md:text-3xl font-light mb-6">{s[lg].short}</h2>
                  <div className="flex flex-wrap gap-3">
                    {CITIES.map((c) => (
                      <Link key={c.id} to={pagePath(lg, s[lg].slug, c.id)}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-vm-border text-vm-text2 hover:border-vm-accent hover:text-vm-text transition-colors"
                        data-testid={`hub-link-${s.id}-${c.id}`}>
                        {c[lg].name}
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <Link to={hubPath(lg === "es" ? "en" : "es")} className="vm-label inline-block mt-16 hover:text-vm-text" data-testid="hub-lang-switch">
              {lg === "es" ? "English version →" : "Versión en español →"}
            </Link>
          </Reveal>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileNav />
    </div>
  );
}
